/**
 * Монітор проведень (ч.793, рішення власника): реєстр НЕпроведених
 * документів усіх типів — те, що ще треба провести. Канон Норми #29:
 * .card + InfiniteDocGrid + фільтри рядком; «Усі організації» — порожній
 * фільтр org. Мігровані з 1С приховані (перемикач).
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColDef } from 'ag-grid-community';
import { orgs } from '@/api/catalogs';
import { postingMonitor, type UnpostedRow } from '@/api/postingMonitor';
import DatePopover from '@/components/DatePopover';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import { useDefaultPeriod, useOrgFilter } from '@/store/defaults';
import { DOC_TABLE_NAMES_UK } from '@/utils/moduleNames';

const typeName = (t: string) => DOC_TABLE_NAMES_UK[t] ?? t;
const fmt2 = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PostingMonitorPage() {
  const nav = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const [orgId, setOrgId] = useOrgFilter();
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgList.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgList.data]);
  const [fltType, setFltType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // ч.1258: з майстра перенесення сюди ведуть з «?migrated=1» — одразу з перенесеними.
  // ч.1307 (власник: з чек-апу «непроведені перенесені» кидало в історію міграцій): чек-ап і
  // майстер ведуть сюди з «&period=all&org=…» — перенесені документи історичні, у періоді за
  // замовчуванням з шапки їх немає; організація — та, для якої робили чек-ап.
  const [params] = useSearchParams();
  const [migrated, setMigrated] = useState(params.get('migrated') === '1');
  const [allTime, setAllTime] = useState(params.get('period') === 'all');
  const orgParam = params.get('org');
  useEffect(() => { if (orgParam) setOrgId(orgParam); }, [orgParam, setOrgId]);
  // Дати: порожньо → період за замовчуванням з шапки (ч.775); «за весь час» — без обмеження.
  const { dateFrom: gFrom, dateTo: gTo } = useDefaultPeriod();
  const dFrom = allTime ? '' : (dateFrom || (gFrom ? gFrom.slice(0, 10) : ''));
  const dTo = allTime ? '' : (dateTo || (gTo ? gTo.slice(0, 10) : ''));
  const [catalog, setCatalog] = useState<{ group: string; types: string[] }[]>([]);

  const { datasource, total } = useInfiniteGridDatasource<UnpostedRow>(
    async (limit, offset, sort) => {
      const p = await postingMonitor.unposted({
        organization_id: orgId || undefined,
        document_type: fltType || undefined,
        date_from: dFrom || undefined,
        date_to: dTo || undefined,
        include_migrated: migrated || undefined,
        sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
        limit, offset,
      });
      setCatalog(p.catalog ?? []);
      return { items: p.items, total: p.total };
    },
    [orgId, fltType, dFrom, dTo, migrated],
  );

  const cols = useMemo<ColDef<UnpostedRow>[]>(() => [
    {
      colId: 'date', field: 'date', headerName: 'Дата', width: 110,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '—',
    },
    {
      colId: 'type', field: 'document_type', headerName: 'Документ', width: 240,
      valueFormatter: (p) => p.value ? typeName(p.value) : '',
    },
    {
      colId: 'number', field: 'number', headerName: '№', width: 150,
      cellRenderer: (p: { data?: UnpostedRow }) => {
        const r = p.data;
        if (!r) return '';
        const route = DOC_ROUTES[r.document_type];
        return route ? (
          <a style={{ cursor: 'pointer' }} title="Відкрити документ"
            onClick={() => nav(route(r.document_id))}>
            {r.number ?? 'відкрити'}
          </a>
        ) : (r.number ?? '—');
      },
    },
    {
      colId: 'contractor', field: 'contractor_name', headerName: 'Контрагент',
      flex: 1, minWidth: 180, valueFormatter: (p) => p.value ?? '—',
    },
    {
      colId: 'amount', field: 'amount', headerName: 'Сума', width: 140,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt2(Number(p.value)) : '',
    },
    {
      colId: 'organization', field: 'organization_id', headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
    },
  ], [orgName, nav]);

  return (
    <div className="card">
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
        <h2 style={{ margin: 0 }}>Монітор проведень<TitleTotal total={total} /></h2>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ width: 220, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <select value={fltType} onChange={(e) => setFltType(e.target.value)} style={{ width: 240, flex: '0 0 auto' }}>
          <option value="">Тип документа — усі</option>
          {catalog.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.types.map((t) => <option key={t} value={t}>{typeName(t)}</option>)}
            </optgroup>
          ))}
        </select>
        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flex: '0 0 auto', opacity: allTime ? 0.5 : 1 }}
          title="Дати документа (порожньо — період за замовчуванням з шапки)">
          <DatePopover value={dateFrom} onChange={(v) => { setDateFrom(v); setAllTime(false); }} style={{ width: 108 }} />
          <span style={{ color: 'var(--muted)' }}>—</span>
          <DatePopover value={dateTo} onChange={(v) => { setDateTo(v); setAllTime(false); }} style={{ width: 108 }} />
        </span>
        <label style={{ cursor: 'pointer', fontSize: '0.85rem', flex: '0 0 auto' }}
          title="Не обмежувати датами документа (перенесені з 1С документи — за всі роки)">
          <input type="checkbox" checked={allTime} onChange={(e) => setAllTime(e.target.checked)}
            style={{ width: 'auto', marginRight: 4 }} />
          за весь час
        </label>
        <label style={{ cursor: 'pointer', fontSize: '0.85rem', flex: '0 0 auto' }}
          title="Показати також непроведені документи, імпортовані з 1С">
          <input type="checkbox" checked={migrated} onChange={(e) => setMigrated(e.target.checked)}
            style={{ width: 'auto', marginRight: 4 }} />
          з 1С
        </label>
      </div>
      <InfiniteDocGrid<UnpostedRow>
        datasource={datasource}
        columnDefs={cols}
        storageKey="posting-monitor"
        onRowDoubleClicked={(r) => { const route = DOC_ROUTES[r.document_type]; if (route) nav(route(r.document_id)); }}
      />
    </div>
  );
}
